/**
 * MSP-Kontexte (Provisioning-Contexts, N3-4) — operator-only Verwaltung.
 *
 * Ein Kontext ist die benannte, brand-skopierte Gruppierung, unter der
 * Mandanten provisioniert werden (IONOS-PSS-Modell). Diese Seite listet alle
 * Kontexte, erlaubt Anlegen, Umbenennen/Archivieren und Löschen.
 *
 * Muster bewusst identisch zu FederationTargetsPage: Tabelle + refresh() +
 * Inline-Upsert-Form + axios CRUD + Fehler-Banner. Read-only-Operatoren sehen
 * alle mutierenden Buttons deaktiviert (operator.readOnly aus useAuth()).
 *
 * Backend: GET/POST /api/contexts, PATCH/DELETE /api/contexts/:id
 * (provisioning-context.routes.ts).
 */
import { useEffect, useState, FormEvent } from 'react';
import api, { ContextView, extractApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { labelForContextStatus } from '../services/status-labels';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-slate-200 text-slate-600',
};

export function ContextsPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<ContextView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh(): void {
    setLoading(true);
    api.get<ContextView[]>('/contexts')
      .then((r) => setRows(r.data))
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, []);

  async function rename(ctx: ContextView): Promise<void> {
    const name = window.prompt('Neuer Name für den Kontext:', ctx.name);
    if (name === null || name.trim() === '' || name.trim() === ctx.name) return;
    setBusyId(ctx.id);
    setError(null);
    try {
      await api.patch(`/contexts/${ctx.id}`, { name: name.trim() });
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusyId(null);
    }
  }

  async function toggleArchive(ctx: ContextView): Promise<void> {
    const next = ctx.status === 'active' ? 'archived' : 'active';
    setBusyId(ctx.id);
    setError(null);
    try {
      await api.patch(`/contexts/${ctx.id}`, { status: next });
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(ctx: ContextView): Promise<void> {
    if (!window.confirm(
      `Kontext "${ctx.name}" löschen? Zugeordnete Mandanten (${ctx.tenantCount}) laufen weiter, `
      + 'ihre Zuordnung wird nur entfernt.',
    )) return;
    setBusyId(ctx.id);
    setError(null);
    try {
      await api.delete(`/contexts/${ctx.id}`);
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">MSP-Kontexte</h2>
          <p className="text-sm text-slate-500">
            Benannte, brand-skopierte Gruppierungen, unter denen Mandanten
            provisioniert werden. Die Mandanten-Zuordnung erfolgt je Mandant
            in der Tenant-Detailansicht.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-primary"
          disabled={isReadOnly}
        >
          {showForm ? 'Schliessen' : 'Kontext anlegen'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {showForm && !isReadOnly && (
        <CreateForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
          onError={setError}
        />
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Marke</th>
              <th className="p-3">Externe Ref.</th>
              <th className="p-3">Status</th>
              <th className="p-3">Mandanten</th>
              <th className="p-3">Angelegt</th>
              <th className="p-3">Aktionen</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((ctx) => (
              <tr key={ctx.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{ctx.name}</td>
                <td className="p-3 font-mono text-slate-600">{ctx.brand}</td>
                <td className="p-3 font-mono text-slate-500">{ctx.externalRef ?? '—'}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[ctx.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {labelForContextStatus(ctx.status)}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{ctx.tenantCount}</td>
                <td className="p-3 text-slate-500" title={ctx.createdAt}>
                  {new Date(ctx.createdAt).toLocaleDateString()}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => rename(ctx)}
                      disabled={busyId === ctx.id || isReadOnly}
                      className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      Umbenennen
                    </button>
                    <button
                      onClick={() => toggleArchive(ctx)}
                      disabled={busyId === ctx.id || isReadOnly}
                      className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      {ctx.status === 'active' ? 'Archivieren' : 'Reaktivieren'}
                    </button>
                    <button
                      onClick={() => remove(ctx)}
                      disabled={busyId === ctx.id || isReadOnly}
                      className="text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                Noch keine MSP-Kontexte angelegt.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface CreateFormProps {
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

const BRAND_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

function CreateForm({ onClose, onSaved, onError }: CreateFormProps) {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('sectepe');
  const [externalRef, setExternalRef] = useState('');
  const [busy, setBusy] = useState(false);

  const nameValid = name.trim().length > 0 && name.length <= 120;
  const brandValid = BRAND_RE.test(brand);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!nameValid || !brandValid) {
      onError('Name (1–120 Zeichen) und Marke (Kleinbuchstaben-Slug) erforderlich.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/contexts', {
        name: name.trim(),
        brand,
        externalRef: externalRef.trim() === '' ? undefined : externalRef.trim(),
      });
      onSaved();
    } catch (err) {
      onError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white shadow rounded p-4 mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Kontext anlegen</h3>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">×</button>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. IONOS PSS Account 123"
          maxLength={120}
          className="w-full px-2 py-1 rounded border text-sm"
          required
        />
        {!nameValid && name.length > 0 && (
          <p className="text-xs text-red-600 mt-1">1 bis 120 Zeichen erforderlich.</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Marke</label>
        <input
          value={brand}
          onChange={(e) => setBrand(e.target.value.toLowerCase())}
          placeholder="sectepe"
          className="w-full px-2 py-1 rounded border font-mono text-sm"
          required
        />
        {!brandValid && (
          <p className="text-xs text-red-600 mt-1">
            Kleinbuchstaben-Slug erwartet (a-z0-9-, 2–32 Zeichen).
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Externe Referenz (optional)</label>
        <input
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
          placeholder="z.B. CRM-/Commerce-ID"
          maxLength={128}
          className="w-full px-2 py-1 rounded border text-sm"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-1 rounded border" disabled={busy}>
          Abbrechen
        </button>
        <button type="submit" className="btn-primary" disabled={busy || !nameValid || !brandValid}>
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}
